import React, { useState, useEffect } from 'react';
import { ShoppingBag, ExternalLink, Sparkles, Shirt } from 'lucide-react';
import { getApiUrl, resolveImageUrl } from '../utils/api';
import '../styles/storefront.css';

const Storefront = () => {
  const [products, setProducts] = useState([]);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = getApiUrl();
        const [productsRes, businessRes] = await Promise.all([
          fetch(`${apiUrl}/api/storefront-products`),
          fetch(`${apiUrl}/api/business-info`)
        ]);
        const [productsData, businessData] = await Promise.all([
          productsRes.json(),
          businessRes.json()
        ]);
        setProducts(productsData);
        setBusinessInfo(businessData);
      } catch (err) {
        console.error('Error loading storefront:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const storefrontUrl =
    businessInfo?.amazon_storefront_url ||
    'https://www.amazon.com/shop/glamoverall';
  const macUrl =
    businessInfo?.mac_storefront_url ||
    'https://community.maccosmetics.com/a/apolonia';
  const aeUrl = businessInfo?.ae_storefront_url || '';

  if (loading) {
    return (
      <div className="storefront-page">
        <div className="marble-background"></div>
        <div className="storefront-loading">
          <p>Loading curated favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="storefront-page" data-testid="storefront-page">
      <div className="marble-background"></div>

      {/* Hero */}
      <section className="storefront-hero">
        <div className="container-centered">
          <div className="storefront-hero-inner">
            <div className="storefront-eyebrow">
              <Sparkles size={16} className="icon-gold" />
              <span>The Edit</span>
              <Sparkles size={16} className="icon-gold" />
            </div>
            <h1 className="storefront-title">Shop With Me</h1>
            <p className="storefront-subtitle">
              A handpicked collection of the products I personally love and
              recommend &mdash; the everyday essentials, beauty must-haves, and
              little luxuries that earn a permanent spot in my routine.
            </p>
            <div className="storefront-cta-row">
              <a
                href={storefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="storefront-hero-cta"
                data-testid="storefront-hero-amazon-link"
              >
                <ShoppingBag size={18} />
                <span>Shop My Amazon Storefront</span>
                <ExternalLink size={16} />
              </a>
              {macUrl && (
                <a
                  href={macUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="storefront-hero-cta storefront-hero-cta-mac"
                  data-testid="storefront-hero-mac-link"
                >
                  <Sparkles size={18} />
                  <span>Shop MAC With Me</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {aeUrl && (
                <a
                  href={aeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="storefront-hero-cta storefront-hero-cta-ae"
                  data-testid="storefront-hero-ae-link"
                >
                  <Shirt size={18} />
                  <span>Shop American Eagle With Me</span>
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="storefront-products-section">
        <div className="container-centered">
          {products.length === 0 ? (
            <div className="storefront-empty">
              <h3 className="storefront-empty-title">A New Edit Is Coming Soon</h3>
              <p className="storefront-empty-text">
                I&apos;m curating my favorite products right now. In the
                meantime, browse the full Amazon storefront for everything
                I&apos;ve been loving.
              </p>
              <a
                href={storefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="storefront-empty-cta"
                data-testid="storefront-empty-amazon-link"
              >
                <ShoppingBag size={18} />
                <span>Shop The Full Storefront</span>
              </a>
            </div>
          ) : (
            <>
              <div className="storefront-section-header">
                <h2 className="storefront-section-title">
                  Apolonia&apos;s Picks
                </h2>
                <p className="storefront-section-subtitle">
                  Suggested products, tried &amp; loved
                </p>
              </div>

              <div className="storefront-grid" data-testid="storefront-grid">
                {products.map((product) => (
                  <article
                    key={product.id}
                    className="product-card"
                    data-testid={`product-card-${product.id}`}
                  >
                    <a
                      href={product.amazon_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-card-image-link"
                    >
                      <div className="product-card-arch">
                        <div className="product-card-image-wrap">
                          {product.image ? (
                            <img
                              src={resolveImageUrl(product.image)}
                              alt={product.name}
                              className="product-card-image"
                              loading="lazy"
                            />
                          ) : (
                            <div className="product-card-placeholder">
                              <ShoppingBag size={36} className="icon-gold" />
                            </div>
                          )}
                        </div>
                      </div>
                    </a>

                    <div className="product-card-body">
                      <h3 className="product-card-name">{product.name}</h3>
                      {product.note && (
                        <p className="product-card-note">{product.note}</p>
                      )}
                      <a
                        href={product.amazon_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="product-card-cta"
                        data-testid={`product-card-cta-${product.id}`}
                      >
                        <span>Shop on Amazon</span>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Closing CTA Banner */}
      <section className="storefront-banner-section">
        <div className="container-centered">
          <div className="storefront-banner">
            <h3 className="storefront-banner-title">Explore Everything I Love</h3>
            <p className="storefront-banner-text">
              See the complete collection of suggested products, beauty staples,
              and lifestyle essentials &mdash; from Amazon to MAC Cosmetics to
              American Eagle.
            </p>
            <div className="storefront-cta-row">
              <a
                href={storefrontUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="storefront-banner-cta"
                data-testid="storefront-banner-amazon-link"
              >
                <ShoppingBag size={18} />
                <span>My Amazon Storefront</span>
                <ExternalLink size={16} />
              </a>
              {macUrl && (
                <a
                  href={macUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="storefront-banner-cta storefront-banner-cta-mac"
                  data-testid="storefront-banner-mac-link"
                >
                  <Sparkles size={18} />
                  <span>Shop MAC With Me</span>
                  <ExternalLink size={16} />
                </a>
              )}
              {aeUrl && (
                <a
                  href={aeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="storefront-banner-cta storefront-banner-cta-ae"
                  data-testid="storefront-banner-ae-link"
                >
                  <Shirt size={18} />
                  <span>Shop American Eagle With Me</span>
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Storefront;
